import React, { useState, useEffect, useCallback } from "react";
import { api } from "../../api";
import { Icon, Empty, Spinner } from "../../components/ui";
import { derive } from "../admin/recordDerive";

const STAGE = { SUBMISSION: "Submission", END_PROCESS: "End Process", MODIFICATION: "Modification", MORE_INFO: "More Info" };
const pad = (n) => String(n).padStart(2, "0");
const fmt = (t) => { try { if (!t) return "—"; const d = new Date(t); return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()}`; } catch { return "—"; } };

export function AuditorView({ user }) {
  const [tasks, setTasks] = useState(null);
  const [openId, setOpenId] = useState(null);   // audit task being viewed
  const [rec, setRec] = useState(null);
  const [loadingRec, setLoadingRec] = useState(false);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    try { setTasks(await api.auditList()); } catch { setTasks([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openTask = async (t) => {
    setOpenId(t.id); setRec(null); setLoadingRec(true);
    try { const r = await api.auditRecord(t.recordId); setRec({ ...r, __task: t }); }
    catch { setRec(null); } finally { setLoadingRec(false); }
  };

  if (tasks === null) return <div className="empty"><Spinner /></div>;

  // ---- full-page record view ----
  if (openId) {
    if (loadingRec) return <div className="empty"><Spinner /></div>;
    if (!rec) return <div className="audit-page"><button className="btn btn-ghost btn-sm" onClick={() => setOpenId(null)}>← Back to audit list</button><Empty title="Couldn't load the record" /></div>;
    const d = derive(rec) || {};
    const t = rec.__task || {};
    const kv = (label, val) => (val === "" || val == null || val === "—") ? null : <div className="au-kv"><span>{label}</span><b>{val}</b></div>;
    return (
      <div className="audit-page">
        <div className="au-bar">
          <button className="btn btn-ghost btn-sm" onClick={() => setOpenId(null)}><Icon.chevron width="15" height="15" style={{ transform: "rotate(180deg)" }} /> Back to audit list</button>
          <div className="au-title"><b>PID {rec.mrn}</b> · {rec.orderName || d.orders || ""}</div>
        </div>

        <div className="au-flags">
          <span className="au-flag">Worked by <b>{t.worker}</b></span>
          <span className="au-flag">Process <b>{STAGE[t.process] || t.process}</b></span>
          <span className="au-flag">Status set <b>{t.status || "—"}</b></span>
          {t.status === "Reviewed" && t.reviewedDays != null && (
            <span className="au-flag au-flag-warn">Kept in Reviewed for <b>{t.reviewedDays} day{t.reviewedDays === 1 ? "" : "s"}</b></span>
          )}
        </div>

        <div className="au-grid">
          <section className="au-card">
            <h3 className="au-h">Record</h3>
            <div className="au-kvs">
              {kv("PID (MRN)", rec.mrn)}{kv("Unique ID", rec.uniqueId)}
              {kv("Group", rec.productionGroup || d.location)}{kv("Clinic location", d.clinic)}
              {kv("Order", rec.orderName)}{kv("Order date", d.orderDate)}{kv("RTC date", d.rtcDate)}
              {kv("Urgent / Routine", d.urgentRoutine)}{kv("Clinic priority", d.clinicPriority)}
              {kv("Payor / IPA", d.insurance)}{kv("Portal", d.portal)}
            </div>
          </section>
          <section className="au-card">
            <h3 className="au-h">What the user entered</h3>
            <div className="au-kvs">
              {kv("Submitted via", rec.submissionMethod)}{kv("Submitter group", rec.submitterGroup)}{kv("Submitter name", rec.submitterName)}
              {kv("CPTs", rec.submittedCpts)}{kv("ICDs", rec.submittedIcds)}
              {kv("Status", rec.paStatus)}{kv("Inbox", rec.inboxValue)}
              {kv("Authorization number", rec.authNumber)}{kv("Request number", rec.requestNumber)}
              {kv("Fax", rec.faxNumber)}{kv("Phone", rec.phoneNumber)}
              {kv("Reason", rec.reason)}{kv("Remarks", rec.remarks || rec.uploaderNote)}
              {rec.reviewedSince && kv("In Reviewed since", fmt(rec.reviewedSince))}
            </div>
          </section>
        </div>

        <AuditHistory raw={rec.workHistory} />
      </div>
    );
  }

  // ---- audit list ----
  const filtered = (tasks || []).filter((t) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return [t.worker, t.status, STAGE[t.process], String(t.recordId)].some((v) => (v || "").toString().toLowerCase().includes(s));
  });

  return (
    <div className="jr-page">
      <div className="jr-head-bar">
        <div><h2 style={{ margin: 0 }}>Records to audit</h2><div className="cell-sub">Click a record to review how the user filled it in.</div></div>
        <div className="inv-search"><Icon.search width="15" height="15" /><input className="inp" placeholder="Search user, status…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
      </div>
      {filtered.length === 0
        ? <Empty title="Nothing to audit yet" text="When an admin sends records for audit, they'll appear here." />
        : <div className="card cmd-card"><div className="table-wrap"><table className="tbl cmd-table">
            <thead><tr><th>User</th><th>Process</th><th>Status set</th><th style={{ textAlign: "center" }}>Days in Reviewed</th><th>Sent by</th><th>Sent on</th></tr></thead>
            <tbody>
              {filtered.map((t) => (
                <tr key={t.id} className="row-click" onClick={() => openTask(t)} title="Click to review">
                  <td className="cell-strong">{t.worker}</td>
                  <td className="cell-sub">{STAGE[t.process] || t.process}</td>
                  <td><b>{t.status || "—"}</b></td>
                  <td style={{ textAlign: "center" }}>{t.status === "Reviewed" && t.reviewedDays != null ? <span className="rv-badge rv-amber">{t.reviewedDays}d</span> : "—"}</td>
                  <td className="cell-sub">{t.createdBy}</td>
                  <td className="cell-sub">{fmt(t.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table></div></div>}
    </div>
  );
}

function AuditHistory({ raw }) {
  let rounds = [];
  try { rounds = raw ? JSON.parse(raw) : []; } catch { rounds = []; }
  if (!Array.isArray(rounds) || !rounds.length) return null;
  const view = [...rounds].reverse();
  return (
    <section className="au-card" style={{ marginTop: 14 }}>
      <h3 className="au-h">How it was worked (each round)</h3>
      <div className="jr-wrap">
        {view.map((h, i) => (
          <div className="jr-card" key={i}>
            <div className="jr-head">
              <span className="jr-stage">{STAGE[h.stage] || h.stage || "—"}</span>
              {h.status && <span className="jr-status">{h.status}</span>}
              <span className="jr-spacer" /><span className="jr-by">{h.by || "A user"}</span><span className="jr-when">{h.date || ""}</span>
            </div>
            <div className="jr-body">
              {h.inbox && <span><i>Inbox</i>{h.inbox}</span>}
              {h.requestNumber && <span><i>Request #</i>{h.requestNumber}</span>}
              {h.authNumber && <span><i>Auth #</i>{h.authNumber}</span>}
              {h.cpts && <span><i>CPTs</i>{h.cpts}</span>}
              {h.icds && <span><i>ICDs</i>{h.icds}</span>}
              {h.reason && <span><i>Reason</i>{h.reason}</span>}
              {h.remarks && <span><i>Remarks</i>{h.remarks}</span>}
              {h.issueType && <span><i>Issue</i>{h.issueType}{h.issueReason ? ` — ${h.issueReason}` : ""}</span>}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}